
import React from "react";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { CheckCircle, ChevronRight, Circle, Check } from "lucide-react";
import { Link } from "react-router-dom";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

export interface SetupStep {
  id: string;
  name: string;
  completed: boolean;
  path: string;
}

interface SetupProgressTrackerProps {
  steps: SetupStep[];
  className?: string;
}

export const SetupProgressTracker = ({
  steps,
  className,
}: SetupProgressTrackerProps) => {
  const completedSteps = steps.filter((step) => step.completed).length;
  const totalSteps = steps.length;
  const progressPercentage = 
    totalSteps > 0 ? Math.round((completedSteps / totalSteps) * 100) : 0;

  // Find the first incomplete step to highlight as next action
  const nextStep = steps.find((step) => !step.completed);

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={`flex items-center gap-2 ${className} ${completedSteps > 0 ? 'border-green-500/30' : ''}`}
        >
          <div className="hidden md:flex items-center gap-2">
            <span className="text-sm font-medium">
              Setup Progress: {completedSteps}/{totalSteps}
            </span>
          </div>
          <Progress
            value={progressPercentage}
            className={`w-24 h-2 ${completedSteps > 0 ? 'bg-green-100' : ''}`}
            aria-label="Setup progress"
          />
          <ChevronRight className="h-4 w-4 text-muted-foreground" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-96 p-0" align="end">
        <div className="p-4 border-b">
          <h3 className="font-medium text-lg">Device Management Setup</h3>
          <p className="text-sm text-muted-foreground mt-1">
            {progressPercentage === 100
              ? "All setup steps completed! 🎉"
              : `${completedSteps} of ${totalSteps} steps completed (${progressPercentage}%)`}
          </p>
          {progressPercentage > 0 && progressPercentage < 100 && (
            <Progress 
              value={progressPercentage} 
              className="mt-2 h-2" 
              aria-label="Setup progress detail" 
            />
          )}
        </div>
        <div className="p-2">
          <ul className="space-y-1">
            {steps.map((step) => (
              <li key={step.id}>
                <Link to={step.path}>
                  <Button
                    variant="ghost"
                    className={`w-full justify-start text-left gap-2 h-auto py-3 ${
                      step.completed ? 'border-l-2 border-green-500 pl-3' : ''
                    }`}
                  >
                    {step.completed ? (
                      <CheckCircle className="h-5 w-5 text-green-500 shrink-0" />
                    ) : (
                      <Circle className="h-5 w-5 text-muted-foreground shrink-0" />
                    )}
                    <div>
                      <span className="font-medium block whitespace-pre-wrap">{step.name}</span>
                      <span className={`text-xs block ${step.completed ? 'text-green-600' : 'text-muted-foreground'}`}>
                        {step.completed ? "Completed" : "Pending"}
                      </span>
                    </div>
                    {!step.completed && (
                      <ChevronRight className="ml-auto h-4 w-4 text-muted-foreground" />
                    )}
                    {step.completed && (
                      <Check className="ml-auto h-4 w-4 text-green-500" />
                    )}
                  </Button>
                </Link>
              </li>
            ))}
          </ul>
        </div>
        {nextStep && (
          <div className="p-4 border-t bg-muted/50">
            <Button asChild size="sm" className="w-full">
              <Link to={nextStep.path} className="flex items-center justify-center">
                <span className="text-sm">Configure APNS</span>
                <ChevronRight className="ml-1 h-3 w-3" />
              </Link>
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};
